import * as z from "zod";

// Kept free of React and the API client: assistantSessionRequest seeds this
// from AuthProvider's deep-link prefetch, which loads in the root bundle.

/** `unknown` until the first poll answers. */
export type SubSessionActivity = "unknown" | "working" | "idle" | "failed";

export interface PollAnswer {
  activity: SubSessionActivity;
  activityLabel: string | null;
  waitingOnModes: readonly string[] | null;
  spendPausedCount: number;
  wakeupAt: string | null;
  reviewInterrupted: string | null;
  failureKind: string | null;
  /** When the answer was requested (epoch ms); 0 before the first. */
  requestedAt: number;
  /** The server rejected the session (403/404, e.g. deleted); polling has stopped. */
  rejected: boolean;
}

export const NO_ANSWER: PollAnswer = {
  activity: "unknown",
  activityLabel: null,
  waitingOnModes: null,
  spendPausedCount: 0,
  wakeupAt: null,
  reviewInterrupted: null,
  failureKind: null,
  requestedAt: 0,
  rejected: false,
};

/** A child's `?status_only=1` payload, and its entry in the parent's `sub_session_statuses`. */
export type SubSessionStatus = z.infer<typeof SubSessionStatus>;
export const SubSessionStatus = z.object({
  status: z.string().optional(),
  is_streaming: z.boolean().optional(),
  run_status: z.string().optional(),
  activity_label: z.string().nullish(),
  waiting_sub_sessions: z
    .array(
      z.object({
        mode: z.string(),
        status: z.string(),
        paused_reason: z.string().nullish(),
      }),
    )
    .optional(),
  wakeup_at: z.string().nullish(),
  review_interrupted: z.string().nullish(),
  failure_kind: z.string().nullish(),
});

const ParentSubSessionStatuses = z.object({
  sub_session_statuses: z.record(z.string(), SubSessionStatus),
});

export function answerFromStatus(
  session: SubSessionStatus,
  requestedAt: number,
): PollAnswer {
  const working =
    session.is_streaming === true ||
    session.status === "in_progress" ||
    session.run_status === "working";
  // A dead session's terminal error message can derive an idle run.
  const failed = session.run_status === "failed" || session.status === "dead";
  const isWaiting = session.run_status === "waiting";
  // Settled children stay listed until their wake-up reaches this session,
  // so only still-running ones name what it waits on.
  const unsettled = (session.waiting_sub_sessions ?? []).filter(
    (child) => child.status === "working" || child.status === "waiting",
  );
  const spendPaused = unsettled.filter(
    (child) => child.paused_reason === "spend_limit",
  );
  return {
    activity: failed ? "failed" : working ? "working" : "idle",
    activityLabel: session.activity_label ?? null,
    waitingOnModes: isWaiting
      ? unsettled
          .filter((child) => child.paused_reason !== "spend_limit")
          .map((child) => child.mode)
      : null,
    spendPausedCount: isWaiting ? spendPaused.length : 0,
    wakeupAt: isWaiting ? (session.wakeup_at ?? null) : null,
    reviewInterrupted: session.review_interrupted ?? null,
    failureKind: session.failure_kind ?? null,
    requestedAt,
    rejected: false,
  };
}

/**
 * Last answer per session, so a card renders its status row on mount instead
 * of blanking until its first poll: seeded by the parent's snapshot and kept
 * across chat switches.
 */
const lastAnswers = new Map<string, PollAnswer>();
const MAX_REMEMBERED_ANSWERS = 200;

export function lastAnswer(sessionId: string): PollAnswer | undefined {
  return lastAnswers.get(sessionId);
}

// Several cards and snapshots can report one child, so an older request may answer last.
export function rememberAnswer(sessionId: string, answer: PollAnswer) {
  const known = lastAnswers.get(sessionId);
  if (known && known.requestedAt > answer.requestedAt) return;
  // Re-inserting keeps Map order least-recently-written first for eviction.
  lastAnswers.delete(sessionId);
  lastAnswers.set(sessionId, answer);
  if (lastAnswers.size > MAX_REMEMBERED_ANSWERS) {
    const oldest = lastAnswers.keys().next().value;
    if (oldest !== undefined) lastAnswers.delete(oldest);
  }
}

export type AnswerListener = (answer: PollAnswer) => void;

export function subscribe(
  registry: Map<string, Set<AnswerListener>>,
  sessionId: string,
  listener: AnswerListener,
): () => void {
  let listeners = registry.get(sessionId);
  if (!listeners) {
    listeners = new Set();
    registry.set(sessionId, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) registry.delete(sessionId);
  };
}

/** Every mounted poller per session, so one poller's answer or a parent snapshot shows on all. */
export const answerListeners = new Map<string, Set<AnswerListener>>();

export function announceAnswer(sessionId: string, answer: PollAnswer) {
  answerListeners.get(sessionId)?.forEach((notify) => notify(answer));
}

/** Seeds every child's answer from a parent session snapshot requested at `requestedAt`. */
export function rememberSubSessionStatuses(
  snapshot: unknown,
  requestedAt: number,
) {
  const parsed = ParentSubSessionStatuses.safeParse(snapshot);
  // Without statuses the cards still render from their first poll.
  if (!parsed.success) return;
  for (const [sessionId, status] of Object.entries(
    parsed.data.sub_session_statuses,
  )) {
    if ((lastAnswer(sessionId)?.requestedAt ?? 0) > requestedAt) continue;
    const answer = answerFromStatus(status, requestedAt);
    rememberAnswer(sessionId, answer);
    announceAnswer(sessionId, answer);
  }
}
